const DEFAULT_BATCH_SIZE = 50;

// Process a list in limited concurrent batches to avoid spiking the API.
const buildBatchPromises = (batch, baseIndex, handler, options, progressState) => {
	const { onProgress, onError, continueOnError } = options;
	const reportProgress = () => {
		progressState.processed += 1;
		if (onProgress) {
			onProgress({ processed: progressState.processed, total: progressState.total });
		}
	};

	return batch.map((item, index) => {
		const itemIndex = baseIndex + index;
		return handler(item, itemIndex).then(result => {
			reportProgress();
			return result;
		}).catch(error => {
			reportProgress();
			if (onError) {
				onError({ error, item, index: itemIndex });
			}
			if (!continueOnError) {
				throw error;
			}
			return undefined;
		});
	});
};

const processInBatches = async (items, handler, options = {}) => {
	const safeItems = Array.isArray(items) ? items : [];
	const resolvedHandler = handler;
	const resolvedBatchSize = Number.isFinite(options.batchSize) && options.batchSize > 0
		? options.batchSize
		: DEFAULT_BATCH_SIZE;
	const { onProgress, onError, continueOnError = false } = options;

	if (typeof resolvedHandler !== 'function') {
		throw new Error('processInBatches requires a handler function.');
	}

	const total = safeItems.length;
	const progressState = { processed: 0, total };
	const results = [];

	if (onProgress) {
		onProgress({ processed: progressState.processed, total: progressState.total });
	}

	for (let i = 0; i < total; i += resolvedBatchSize) {
		const batch = safeItems.slice(i, i + resolvedBatchSize);
		const batchResults = await Promise.all(buildBatchPromises(
			batch,
			i,
			resolvedHandler,
			{
				onProgress,
				onError,
				continueOnError,
			},
			progressState,
		));
		results.push(...batchResults);
	}

	return results;
};

export default processInBatches;
